// Stateful controller around the Twilio Voice SDK Device.
// Lives outside the React tree so the Device instance can outlive component
// mounts during a call (the floating call card may unmount/remount as the user
// navigates while the call is still active). Drives the Redux slice via
// dispatched actions; components read state by selecting from the slice.
//
// Lifecycle: very-lazy — Device is constructed inside startCall and torn down
// in hangup / on terminal events. There is no module-level Device that lingers
// between calls.

import { Device } from '@twilio/voice-sdk';

import { store, dispatch } from 'store';
import {
    callFetchingToken,
    callConnecting,
    callRinging,
    callInProgress,
    callEnded,
    callRemoteDisconnect,
    callError,
    callErrorRefined,
    setMuted as setMutedAction,
    denyRecordingConsentPending,
    denyRecordingConsentSucceeded,
    denyRecordingConsentFailed,
    setAudioDevices,
    setSelectedInputDeviceId,
    setSelectedOutputDeviceId,
    setOutputSelectionSupported,
    callOutcomeSettling,
    callOutcomeUpdated,
    callOutcomeSaving,
    callOutcomeSaved,
    callOutcomeSaveFailed,
    generatedNoteUpdated,
    generatedNoteCleared,
    callCardDismissed,
    canSendDigitsStatus
} from 'store/slices/twilioOutboundCall';
import { openSnackbar } from 'store/slices/snackbar';
import { getPatientDetails } from 'store/slices/patient';
import {
    fetchTwilioAccessToken,
    fetchGeneratedCareSessionNote,
    fetchOutboundCallStatus,
    denyRecordingConsent as denyRecordingConsentApi,
    setOutboundCallAnsweredBy
} from 'api/twilio';
import {
    friendlyCallError,
    friendlyServerCallError,
    friendlyServerOutcome,
    friendlyUserMediaError,
    describeTwilioError,
    FAILURE,
    ENDED_BEFORE_CONNECT_COPY,
    MIC_TIMEOUT_COPY,
    SERVER_UNREACHABLE_COPY
} from './twilioCallErrors';
import logger from './logger';

// localStorage keys for the user's preferred audio devices. Persisted across
// calls so the next call reuses them without re-prompting. `deviceId` is stable
// per origin once mic permission is granted; if a saved ID is no longer in the
// available list (e.g. headset unplugged), we clear the saved value and fall
// back to the system default.
const LS_INPUT_DEVICE_KEY = 'synergrx.twilio.preferredInputDeviceId';
const LS_SPEAKER_DEVICE_KEY = 'synergrx.twilio.preferredSpeakerDeviceId';

function readPreferredDeviceId(key) {
    try {
        return localStorage.getItem(key);
    } catch (e) {
        return null;
    }
}

function writePreferredDeviceId(key, deviceId) {
    try {
        if (deviceId) {
            localStorage.setItem(key, deviceId);
        } else {
            localStorage.removeItem(key);
        }
    } catch (e) {
        // ignore — quota / privacy mode
    }
}

// Output routing (speakerDevices / ringtoneDevices) relies on HTMLMediaElement's
// setSinkId. Desktop coverage is now broad (Chrome/Edge, Firefox 116+, Safari
// 18.4+), but most mobile browsers still lack it. Detect at module load via
// feature-check rather than UA-sniffing so the picker hides the speaker
// dropdown wherever it isn't actually available.
const OUTPUT_SELECTION_SUPPORTED = typeof HTMLMediaElement !== 'undefined' && 'setSinkId' in HTMLMediaElement.prototype;

/**
 * @typedef {{ deviceId: string, label: string }} AudioDeviceInfo
 */

// Twilio's `device.audio.availableInputDevices` / `availableOutputDevices` Maps
// are empty immediately after `new Device(...)` — they only populate after the
// SDK acquires a mic stream and the first internal deviceChange fires. The
// browser's enumerateDevices() is ready right away once mic permission has
// been granted (which it must be at this point, since we just constructed a
// Device). Drive the picker + saved-ID matching from here instead, otherwise
// the picker renders empty and saved preferences never match.
/**
 * @returns {Promise<{ inputs: AudioDeviceInfo[], outputs: AudioDeviceInfo[] }>}
 */
async function enumerateAudioDevices() {
    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.enumerateDevices) {
        return { inputs: [], outputs: [] };
    }
    try {
        const all = await navigator.mediaDevices.enumerateDevices();
        const inputs = all.filter((d) => d.kind === 'audioinput').map((d) => ({ deviceId: d.deviceId, label: d.label || '' }));
        const outputs = all.filter((d) => d.kind === 'audiooutput').map((d) => ({ deviceId: d.deviceId, label: d.label || '' }));
        return { inputs, outputs };
    } catch (e) {
        return { inputs: [], outputs: [] };
    }
}

// Dev-only fake mode: drives the Redux slice through the call lifecycle on
// timers without touching Twilio. Lets the UI be exercised end-to-end without
// a Twilio account, real creds, or network access. Toggle via env:
//   REACT_APP_TWILIO_FAKE=1               enable
//   REACT_APP_TWILIO_FAKE_OUTCOME=...     answer (default) | review | ring | busy | noanswer | error
//                                         (review = answer, then the post-call "did you speak
//                                         with someone?" prompt instead of a settled type)
//   REACT_APP_TWILIO_FAKE_DELAY_MS=...    base delay between transitions (default 600)
const FAKE_ENABLED = process.env.REACT_APP_TWILIO_FAKE === '1';
const FAKE_OUTCOME = process.env.REACT_APP_TWILIO_FAKE_OUTCOME || 'answer';
const FAKE_DELAY_MS = Number.parseInt(process.env.REACT_APP_TWILIO_FAKE_DELAY_MS, 10) || 600;

/** @type {import('@twilio/voice-sdk').Device | null} */
let device = null;
/** @type {import('@twilio/voice-sdk').Call | null} */
let activeCall = null;
/** @type {(() => Promise<void>) | null} */
let deviceChangeHandler = null;
// Synchronous guard to close the gap between startCall being invoked and
// `device` being assigned. Prevents a second concurrent startCall (UI
// double-click, programmatic re-entry) from passing the `device || activeCall`
// check while the first call's token fetch is still in flight.
let starting = false;

// Per-call flags used to classify how a call ended. The Twilio JS SDK fires
// the same `disconnect` event whether we hung up locally or the remote party
// did, so we shadow it: hangup() flips localHangupRequested before triggering
// the SDK disconnect, and the call's `accept` event flips wasAccepted. Read in
// wireCallEvents to decide between callEnded (local, card vanishes) and
// callRemoteDisconnect (remote, card lingers with a label).
let localHangupRequested = false;
let wasAccepted = false;
// Set by hangup() when invoked while `starting` is true but no Call exists
// yet. The pending startCall checks this flag at each await checkpoint and
// tears down quietly rather than dispatching callConnecting / callError on
// top of the callEnded that hangup() already emitted.
let cancelDuringStart = false;

// Latest input volume (0..1) from the active call's `volume` event. The Twilio
// Voice SDK does not expose a polled volume on device.audio — it emits a
// `volume` event on the Call (~50Hz) once media is flowing. We cache the most
// recent value here and let the picker poll getInputVolume() against it.
let latestInputVolume = 0;

let fakeTimers = [];
let fakeActive = false;

// Generated-note polling. Module-scope, single-slot — survives the call card
// unmount on hangup. Back-to-back calls overwrite any prior pipeline still in
// flight; accepted edge case (pipelines usually settle in seconds).
const NOTE_POLL_INITIAL_DELAY_MS = 5 * 1000;
const NOTE_POLL_INTERVAL_MS = 1 * 1000;
const NOTE_POLL_MAX_ATTEMPTS = 5 * 60;
/** @type {{ callSid: string, clinicId: number, timeoutId: ReturnType<typeof setTimeout> | null, cancelled: boolean } | null} */
let activeNotePoller = null;

function stopNotePolling() {
    if (!activeNotePoller) {
        return;
    }
    logger.info('twilio:outboundcall stopping generated note polling', {
        callSid: activeNotePoller.callSid,
        clinicId: activeNotePoller.clinicId
    });
    activeNotePoller.cancelled = true;
    if (activeNotePoller.timeoutId) {
        clearTimeout(activeNotePoller.timeoutId);
    }
    activeNotePoller = null;
}

// Terminal: note settled either way, or transcript failed / consent denied
// (no note will follow).
function isPipelineTerminal({ transcriptStatus, noteStatus }) {
    return (
        noteStatus === 'READY' ||
        noteStatus === 'FAILED' ||
        noteStatus === 'CONSENT_DENIED' ||
        transcriptStatus === 'FAILED' ||
        transcriptStatus === 'CONSENT_DENIED'
    );
}

async function pollGeneratedNoteOnce(attempt) {
    const poller = activeNotePoller;
    if (!poller || poller.cancelled) {
        return;
    }
    try {
        const response = await fetchGeneratedCareSessionNote(poller.clinicId, poller.callSid);
        if (poller.cancelled || activeNotePoller !== poller) {
            return;
        }
        const data = response?.data ?? {};
        const transcriptStatus = data.transcriptStatus ?? null;
        const rawNoteStatus = data.noteStatus ?? null;
        const text = data.note ?? null;
        // READY-without-text shouldn't happen; coerce to FAILED so the indicator
        // doesn't get stuck on a "ready" state with nothing to append.
        const noteStatus = rawNoteStatus === 'READY' && !text ? 'FAILED' : rawNoteStatus;
        dispatch(generatedNoteUpdated({ transcriptStatus, noteStatus, text }));
        if (isPipelineTerminal({ transcriptStatus, noteStatus })) {
            logger.info('twilio:outboundcall generated note pipeline terminal', {
                callSid: poller.callSid,
                clinicId: poller.clinicId,
                transcriptStatus,
                noteStatus,
                attempts: attempt + 1
            });
            stopNotePolling();
            return;
        }
    } catch (err) {
        // Transient (network / 5xx / 404 while the server row is being inserted).
        // Persistent failures land in the cap below.
        logger.debug('twilio:outboundcall generated note poll attempt failed', {
            callSid: poller.callSid,
            attempt,
            error: err?.message
        });
    }

    if (!activeNotePoller || activeNotePoller !== poller || poller.cancelled) {
        return;
    }
    if (attempt + 1 >= NOTE_POLL_MAX_ATTEMPTS) {
        // Pipeline didn't settle in time — surface as FAILED so the user writes manually.
        logger.warn('twilio:outboundcall generated note polling exceeded max attempts', {
            callSid: poller.callSid,
            clinicId: poller.clinicId,
            attempts: attempt + 1
        });
        dispatch(generatedNoteUpdated({ transcriptStatus: 'FAILED', noteStatus: 'FAILED', text: null }));
        stopNotePolling();
        return;
    }
    poller.timeoutId = setTimeout(() => {
        pollGeneratedNoteOnce(attempt + 1);
    }, NOTE_POLL_INTERVAL_MS);
}

function startGeneratedNotePolling({ callSid, clinicId }) {
    if (!callSid || !clinicId) {
        return;
    }
    logger.info('twilio:outboundcall starting generated note polling', { callSid, clinicId });
    stopNotePolling();
    // Show the indicator immediately rather than waiting for the initial delay.
    dispatch(generatedNoteUpdated({ transcriptStatus: 'PENDING', noteStatus: 'PENDING', text: null }));
    activeNotePoller = { callSid, clinicId, timeoutId: null, cancelled: false };
    activeNotePoller.timeoutId = setTimeout(() => {
        pollGeneratedNoteOnce(0);
    }, NOTE_POLL_INITIAL_DELAY_MS);
}

// Post-error refinement: after a terminal SDK error the widget shows generic
// copy (for server-rejected dials the SDK only ever hears 31000), but the
// Twilio status callback persists the real dial error onto the OutboundCall
// row. Poll the server briefly and swap the card copy for the specific reason
// (13224 unreachable number, 13227 geo permissions, ...). Short-lived and
// bounded — the callback races the SDK error event by a second or two, so a
// few attempts cover it; if the row never flips to FAILED, the SDK copy from
// friendlyCallError stays. Only failures poll: an SDK code the copy module
// already classifies as an outcome (busy, declined, unavailable) lands the
// card in the neutral state with nothing to refine. Module-scope single-slot
// like the note poller.
const ERROR_REFINE_INITIAL_DELAY_MS = 1000;
const ERROR_REFINE_INTERVAL_MS = 1500;
const ERROR_REFINE_MAX_ATTEMPTS = 5;
// Terminal non-FAILED statuses: the server classified the leg differently
// than the SDK error suggests — stop polling, but only once BOTH legs have
// settled. The parent (browser) leg is answered by our TwiML, so a dial-leg
// failure usually ends it COMPLETED while the real reason lands on the child
// leg's row a moment later. What the card then shows depends on the child
// leg: NO_ANSWER / BUSY repaint with the honest outcome (see
// friendlyServerOutcome); COMPLETED / CANCELED keep the SDK copy.
const ERROR_REFINE_STOP_STATUSES = new Set(['COMPLETED', 'BUSY', 'NO_ANSWER', 'CANCELED']);
/** @type {{ callSid: string, clinicId: number, timeoutId: ReturnType<typeof setTimeout> | null, cancelled: boolean } | null} */
let activeErrorRefiner = null;

function stopErrorRefining() {
    if (!activeErrorRefiner) {
        return;
    }
    activeErrorRefiner.cancelled = true;
    if (activeErrorRefiner.timeoutId) {
        clearTimeout(activeErrorRefiner.timeoutId);
    }
    activeErrorRefiner = null;
}

async function refineErrorOnce(attempt) {
    const refiner = activeErrorRefiner;
    if (!refiner || refiner.cancelled) {
        return;
    }
    try {
        const response = await fetchOutboundCallStatus(refiner.clinicId, refiner.callSid);
        if (refiner.cancelled || activeErrorRefiner !== refiner) {
            return;
        }
        const data = response?.data ?? {};
        if (data.status === 'FAILED' || data.childStatus === 'FAILED') {
            // A dial failure can land on either leg: TwiML-level rejections
            // mark the parent row FAILED, while carrier-level failures mark
            // only the child (dialed) leg FAILED — the answered parent leg
            // still ends COMPLETED. The parent-leg callback also often carries
            // no ErrorCode when the failure was on the child leg, so fall back
            // to the child leg's persisted code either way.
            const refined = friendlyServerCallError({ errorCode: data.errorCode ?? data.childErrorCode });
            logger.info('twilio:outboundcall error refined from server', {
                callSid: refiner.callSid,
                errorCode: data.errorCode ?? null,
                errorMessage: data.errorMessage ?? null,
                childStatus: data.childStatus ?? null,
                childErrorCode: data.childErrorCode ?? null,
                attempts: attempt + 1
            });
            // The reducer ignores this if the card is no longer in the error
            // state (dismissed, or a new call started).
            dispatch(callErrorRefined(refined));
            stopErrorRefining();
            return;
        }
        if (ERROR_REFINE_STOP_STATUSES.has(data.status) && ERROR_REFINE_STOP_STATUSES.has(data.childStatus)) {
            // Neither leg FAILED — but if the dialed leg simply went unanswered
            // (or hit a busy line), the SDK error the card is showing was just
            // noise on an ordinary outcome (Twilio's gateway attaches a generic
            // 31000 to some no-answer HANGUPs). Repaint as that outcome — the
            // reducer moves the card out of the error state; for the rest
            // (COMPLETED / CANCELED) keep the SDK copy as before.
            const outcome = friendlyServerOutcome(data.childStatus);
            if (outcome) {
                logger.info('twilio:outboundcall error refined to server outcome', {
                    callSid: refiner.callSid,
                    status: data.status,
                    childStatus: data.childStatus,
                    attempts: attempt + 1
                });
                dispatch(callErrorRefined(outcome));
            } else {
                logger.debug('twilio:outboundcall error refinement stopped — neither leg FAILED', {
                    callSid: refiner.callSid,
                    status: data.status,
                    childStatus: data.childStatus
                });
            }
            stopErrorRefining();
            return;
        }
        // A leg still shows a pre-terminal status (or the child leg's webhook
        // hasn't landed yet — childStatus null never matches the stop set).
        // Fall through to the retry below; the attempt cap bounds the case
        // where no child leg was ever created.
    } catch (err) {
        // 404 (voice webhook hasn't inserted the row yet) or transient
        // network failure — retry until the attempt cap.
        logger.debug('twilio:outboundcall error refinement attempt failed', {
            callSid: refiner.callSid,
            attempt,
            error: err?.message
        });
    }

    if (!activeErrorRefiner || activeErrorRefiner !== refiner || refiner.cancelled) {
        return;
    }
    if (attempt + 1 >= ERROR_REFINE_MAX_ATTEMPTS) {
        logger.debug('twilio:outboundcall error refinement exhausted — keeping SDK copy', {
            callSid: refiner.callSid,
            attempts: attempt + 1
        });
        stopErrorRefining();
        return;
    }
    refiner.timeoutId = setTimeout(() => {
        refineErrorOnce(attempt + 1);
    }, ERROR_REFINE_INTERVAL_MS);
}

function startErrorRefining({ callSid, clinicId }) {
    // No callSid means the browser never reached Twilio — there is no server
    // row to consult (that absence is itself the "session dropped" signal).
    if (!callSid || !clinicId) {
        return;
    }
    stopErrorRefining();
    activeErrorRefiner = { callSid, clinicId, timeoutId: null, cancelled: false };
    activeErrorRefiner.timeoutId = setTimeout(() => {
        refineErrorOnce(0);
    }, ERROR_REFINE_INITIAL_DELAY_MS);
}

// Call-outcome polling: after a connected call ends, the card stays and shows
// what the call was logged as. The type can still move for a while — the AMD
// verdict, the duration and the transcript land at different times, and a
// voicemail under 3 minutes waits on its transcript — so poll the server's
// view until it reports `settled`. Module-scope single-slot like the note
// poller; same 5 minute ceiling.
const OUTCOME_POLL_INITIAL_DELAY_MS = 1000;
const OUTCOME_POLL_INTERVAL_MS = 2 * 1000;
const OUTCOME_POLL_MAX_ATTEMPTS = (5 * 60) / 2;
/** @type {{ callSid: string, clinicId: number, timeoutId: ReturnType<typeof setTimeout> | null, cancelled: boolean } | null} */
let activeOutcomePoller = null;

function stopOutcomePolling() {
    if (!activeOutcomePoller) {
        return;
    }
    activeOutcomePoller.cancelled = true;
    if (activeOutcomePoller.timeoutId) {
        clearTimeout(activeOutcomePoller.timeoutId);
    }
    activeOutcomePoller = null;
}

async function pollOutcomeOnce(attempt) {
    const poller = activeOutcomePoller;
    if (!poller || poller.cancelled) {
        return;
    }
    try {
        const response = await fetchOutboundCallStatus(poller.clinicId, poller.callSid);
        if (poller.cancelled || activeOutcomePoller !== poller) {
            return;
        }
        const data = response?.data ?? {};
        // Every result updates the type the card shows, settled or not. The
        // reducer ignores it once the card is dismissed or a new call started.
        dispatch(
            callOutcomeUpdated({
                outboundCallId: data.outboundCallId ?? null,
                answeredBy: data.answeredBy ?? null,
                answeredBySource: data.answeredBySource ?? null,
                settled: !!data.settled,
                needsReview: !!data.needsReview,
                reviewReason: data.reviewReason ?? null
            })
        );
        if (data.settled) {
            logger.info('twilio:outboundcall call outcome settled', {
                callSid: poller.callSid,
                clinicId: poller.clinicId,
                answeredBy: data.answeredBy ?? null,
                answeredBySource: data.answeredBySource ?? null,
                needsReview: !!data.needsReview,
                reviewReason: data.reviewReason ?? null,
                attempts: attempt + 1
            });
            stopOutcomePolling();
            return;
        }
    } catch (err) {
        // Transient network / 5xx — retry until the cap below.
        logger.debug('twilio:outboundcall call outcome poll attempt failed', {
            callSid: poller.callSid,
            attempt,
            error: err?.message
        });
    }

    if (!activeOutcomePoller || activeOutcomePoller !== poller || poller.cancelled) {
        return;
    }
    if (attempt + 1 >= OUTCOME_POLL_MAX_ATTEMPTS) {
        // Never settled in time: stop and show the last type seen as final,
        // with no prompt. Call History is the way to correct it from here.
        logger.warn('twilio:outboundcall call outcome polling exceeded max attempts', {
            callSid: poller.callSid,
            clinicId: poller.clinicId,
            attempts: attempt + 1
        });
        dispatch(callOutcomeUpdated({ settled: true, needsReview: false }));
        stopOutcomePolling();
        return;
    }
    poller.timeoutId = setTimeout(() => {
        pollOutcomeOnce(attempt + 1);
    }, OUTCOME_POLL_INTERVAL_MS);
}

function startOutcomePolling({ callSid, clinicId }) {
    if (!callSid || !clinicId) {
        return;
    }
    logger.info('twilio:outboundcall starting call outcome polling', { callSid, clinicId });
    stopOutcomePolling();
    // Keeps the card up from this dispatch on, before the first poll returns.
    dispatch(callOutcomeSettling());
    activeOutcomePoller = { callSid, clinicId, timeoutId: null, cancelled: false };
    activeOutcomePoller.timeoutId = setTimeout(() => {
        pollOutcomeOnce(0);
    }, OUTCOME_POLL_INITIAL_DELAY_MS);
}

// Lead time before token expiry at which the Twilio SDK fires `tokenWillExpire`.
// The SDK's default is 10s; 5min gives the network round-trip plenty of slack.
// Twilio's updateToken does not interrupt an in-progress call; the new token
// only refreshes the Device's signaling channel.
const TOKEN_REFRESH_LEAD_MS = 300 * 1000;

// Hard cap on Device.connect(): startCall's finally (which resets `starting`)
// only runs if the awaited connect settles, so a connect that neither resolves
// nor rejects (stalled signaling socket, hung mic acquisition) would wedge
// `starting` true and silently refuse every dial until a page refresh — the
// same failure class the access-token timeout closes on the fetch side.
// The browser's mic-permission prompt no longer lands inside this await —
// startCall's getUserMedia preflight fronts it before the token fetch — so
// this only needs to cover signaling + media setup, but stays generous for
// slow networks.
const CONNECT_TIMEOUT_MS = 45 * 1000;

// Bound on the getUserMedia preflight in startCall. Deny and dismiss both
// reject promptly, so this only bites when the promise never settles at all —
// e.g. Chrome holds the permission prompt (and the promise) pending while the
// tab is backgrounded. The preflight sits before the try/finally that clears
// `starting`, so an unsettled promise would otherwise latch `starting` true
// and wedge every subsequent dial until a refresh — the same class of hang
// CONNECT_TIMEOUT_MS closes on the connect side. Generous enough to read the
// prompt and think about it.
const MIC_PREFLIGHT_TIMEOUT_MS = 30 * 1000;

function resetLifecycleFlags() {
    localHangupRequested = false;
    wasAccepted = false;
    latestInputVolume = 0;
    cancelDuringStart = false;
}

function teardown() {
    latestInputVolume = 0;
    if (activeCall) {
        // Snapshots for the late-error sink below — activeCall is nulled and
        // resetLifecycleFlags may run before the sink ever fires.
        const teardownCallSid = activeCall.parameters?.CallSid ?? null;
        const acceptedAtTeardown = wasAccepted;
        try {
            activeCall.removeAllListeners();
            // A Call can emit a late `error` after its own `disconnect` in the
            // same tick (user-media acquisition failures _disconnect() first,
            // then emit). EventEmitter throws on an `error` emit with zero
            // listeners, which surfaces as an uncaught rejection — keep a sink
            // on the discarded Call. When that ordering is exactly what
            // happened — never accepted, user-media code, card still showing
            // the "Call ended before it connected." remote disconnect — repaint
            // the card with the mapped copy: this is the SDK-side failure of a
            // device that passed (or skipped) startCall's preflight. The
            // status guard keeps a benign late error after a real remote
            // hangup, or one landing on a dismissed card, from flipping state
            // it doesn't own (callError's reducer overwrites unconditionally).
            activeCall.on('error', (err) => {
                const isUserMediaError = err?.code === 31401 || err?.code === 31402;
                const { status } = store.getState().twilioOutboundCall;
                if (!acceptedAtTeardown && isUserMediaError && status === 'remoteEnded') {
                    logger.error('twilio:outboundcall late user-media error — repainting card', {
                        callSid: teardownCallSid,
                        ...describeTwilioError(err)
                    });
                    dispatch(callError(friendlyCallError(err)));
                } else {
                    logger.warn('twilio:outboundcall call error after teardown', {
                        callSid: teardownCallSid,
                        ...describeTwilioError(err)
                    });
                }
            });
        } catch (e) {
            // ignore
        }
        activeCall = null;
    }
    if (device) {
        try {
            if (deviceChangeHandler && device.audio?.off) {
                device.audio.off('deviceChange', deviceChangeHandler);
            }
        } catch (e) {
            // ignore
        }
        deviceChangeHandler = null;
        try {
            device.destroy();
        } catch (e) {
            // ignore
        }
        device = null;
    }
}

async function publishAvailableDevices() {
    const { inputs, outputs } = await enumerateAudioDevices();
    dispatch(setAudioDevices({ inputDevices: inputs, outputDevices: outputs }));
    return { inputs, outputs };
}

// Apply a stored input deviceId to the live Device. Falls back to default and
// clears the stored value if the deviceId is no longer available. Returns the
// id that ended up active (string deviceId or null for default).
/**
 * @param {string | null} preferredId
 * @param {AudioDeviceInfo[]} availableInputs
 * @returns {Promise<string | null>}
 */
async function applyInputDevice(preferredId, availableInputs) {
    if (!device?.audio) {
        return null;
    }
    if (!preferredId) {
        return null;
    }
    const isAvailable = availableInputs.some((d) => d.deviceId === preferredId);
    if (!isAvailable) {
        writePreferredDeviceId(LS_INPUT_DEVICE_KEY, null);
        try {
            await device.audio.unsetInputDevice?.();
        } catch (e) {
            // ignore — falling back to default is best-effort
        }
        return null;
    }
    try {
        await device.audio.setInputDevice(preferredId);
        return preferredId;
    } catch (e) {
        writePreferredDeviceId(LS_INPUT_DEVICE_KEY, null);
        return null;
    }
}

/**
 * @param {string | null} preferredId
 * @param {AudioDeviceInfo[]} availableOutputs
 * @returns {Promise<string | null>}
 */
async function applySpeakerDevice(preferredId, availableOutputs) {
    if (!device?.audio || !OUTPUT_SELECTION_SUPPORTED) {
        return null;
    }
    if (!preferredId) {
        return null;
    }
    const isAvailable = availableOutputs.some((d) => d.deviceId === preferredId);
    if (!isAvailable) {
        writePreferredDeviceId(LS_SPEAKER_DEVICE_KEY, null);
        // Reset to a known-present sink. Twilio's speakerDevices.set accepts an
        // array of ids; an empty array doesn't reliably reset. 'default' is a
        // Chromium convention and not guaranteed, so fall back to the first
        // available output if it's missing.
        const fallback = availableOutputs.find((d) => d.deviceId === 'default') ?? availableOutputs[0];
        if (!fallback) {
            return null;
        }
        try {
            await device.audio.speakerDevices.set([fallback.deviceId]);
            await device.audio.ringtoneDevices.set([fallback.deviceId]);
        } catch (e) {
            return null;
        }
        // If we routed to the system 'default' sink, report null so the picker
        // shows "System default". Otherwise report the actual sink so the
        // picker reflects what audio is really playing through.
        return fallback.deviceId === 'default' ? null : fallback.deviceId;
    }
    try {
        await device.audio.speakerDevices.set([preferredId]);
        await device.audio.ringtoneDevices.set([preferredId]);
        return preferredId;
    } catch (e) {
        writePreferredDeviceId(LS_SPEAKER_DEVICE_KEY, null);
        return null;
    }
}

function notifyAudioFallback() {
    logger.warn('twilio:outboundcall saved audio device unavailable — falling back to default');
    dispatch(
        openSnackbar({
            open: true,
            message: 'Saved audio device unavailable — using system default',
            variant: 'alert',
            alert: { color: 'warning' }
        })
    );
}

async function initializeAudioFromPreferences() {
    if (!device?.audio) {
        return;
    }
    dispatch(setOutputSelectionSupported(OUTPUT_SELECTION_SUPPORTED));
    const { inputs, outputs } = await publishAvailableDevices();

    const savedInputId = readPreferredDeviceId(LS_INPUT_DEVICE_KEY);
    const savedSpeakerId = readPreferredDeviceId(LS_SPEAKER_DEVICE_KEY);

    const activeInputId = await applyInputDevice(savedInputId, inputs);
    const activeSpeakerId = await applySpeakerDevice(savedSpeakerId, outputs);

    dispatch(setSelectedInputDeviceId(activeInputId));
    dispatch(setSelectedOutputDeviceId(activeSpeakerId));

    logger.info('twilio:outboundcall audio devices initialized', {
        outputSelectionSupported: OUTPUT_SELECTION_SUPPORTED,
        inputs,
        outputs,
        savedInputId,
        savedSpeakerId,
        activeInputId,
        activeSpeakerId
    });

    const inputFellBack = !!savedInputId && activeInputId !== savedInputId;
    const speakerFellBack = !!savedSpeakerId && activeSpeakerId !== savedSpeakerId && OUTPUT_SELECTION_SUPPORTED;
    if (inputFellBack || speakerFellBack) {
        notifyAudioFallback();
    }
}

function attachDeviceChangeListener() {
    if (!device?.audio) {
        return;
    }
    deviceChangeHandler = async () => {
        if (!device?.audio) {
            return;
        }
        const { inputs, outputs } = await publishAvailableDevices();

        const previousInputId = readPreferredDeviceId(LS_INPUT_DEVICE_KEY);
        const previousSpeakerId = readPreferredDeviceId(LS_SPEAKER_DEVICE_KEY);
        const inputStillAvailable = !previousInputId || inputs.some((d) => d.deviceId === previousInputId);
        const speakerStillAvailable = !previousSpeakerId || outputs.some((d) => d.deviceId === previousSpeakerId);

        logger.info('twilio:outboundcall audio devices changed', {
            inputs,
            outputs,
            previousInputId,
            previousSpeakerId,
            inputStillAvailable,
            speakerStillAvailable
        });

        if (!inputStillAvailable || (!speakerStillAvailable && OUTPUT_SELECTION_SUPPORTED)) {
            const activeInputId = await applyInputDevice(previousInputId, inputs);
            const activeSpeakerId = await applySpeakerDevice(previousSpeakerId, outputs);
            dispatch(setSelectedInputDeviceId(activeInputId));
            dispatch(setSelectedOutputDeviceId(activeSpeakerId));
            logger.info('twilio:outboundcall audio devices re-applied after change', { activeInputId, activeSpeakerId });
            notifyAudioFallback();
        }
    };
    try {
        device.audio.on('deviceChange', deviceChangeHandler);
    } catch (e) {
        deviceChangeHandler = null;
    }
}

/** @param {import('@twilio/voice-sdk').Call} call */
function wireCallEvents(call) {
    call.on('ringing', () => {
        const callSid = call.parameters?.CallSid ?? null;
        logger.info('twilio:outboundcall ringing', { callSid });
        dispatch(callRinging({ callSid }));
    });
    // Twilio emits `volume` on the Call (~50Hz) once media is flowing. Cache
    // the input value so getInputVolume() returns it — there is no polled
    // device.audio.inputVolume property to read on demand.
    call.on('volume', (inputVolume) => {
        const v = Number(inputVolume);
        latestInputVolume = Number.isFinite(v) ? Math.max(0, Math.min(1, v)) : 0;
    });
    call.on('accept', () => {
        wasAccepted = true;
        const callSid = call.parameters?.CallSid ?? null;
        logger.info('twilio:outboundcall accepted', { callSid });
        dispatch(callInProgress({ callSid, startedAt: Date.now() }));
    });
    // Twilio fires `disconnect` regardless of which side ended the call. We
    // distinguish via localHangupRequested (set by hangup() before invoking
    // the SDK) and wasAccepted (set on the `accept` event) to choose between
    // a silent dismissal and a lingering remoteEnded card.
    //
    // Twilio can emit multiple terminal events for the same call in the same
    // synchronous tick (e.g. `disconnect` followed by `cancel`). EventEmitter
    // snapshots its listener list per-emit, so teardown()'s removeAllListeners
    // does not dedupe events that were already queued. Guard on activeCall —
    // the first terminal event nulls it via teardown(), so any follow-up is a
    // no-op. Also resets the per-call flags so they don't leak forward if a
    // future startCall path skips resetLifecycleFlags.
    const handleTermination = () => {
        if (!activeCall) {
            return;
        }
        // Snapshot before teardown — slice still has clinicId here.
        const acceptedThisCall = wasAccepted;
        const callSid = call.parameters?.CallSid ?? null;
        const { clinicId, recordingConsentDenied } = store.getState().twilioOutboundCall;

        if (localHangupRequested) {
            logger.info('twilio:outboundcall ended locally', { callSid, wasAccepted: acceptedThisCall });
            dispatch(callEnded());
        } else {
            const message = wasAccepted ? 'Patient hung up' : ENDED_BEFORE_CONNECT_COPY;
            logger.info('twilio:outboundcall ended by remote', { callSid, wasAccepted: acceptedThisCall, message });
            dispatch(callRemoteDisconnect({ message }));
        }
        teardown();
        resetLifecycleFlags();

        // A connected call has a call type to show, whoever hung up and
        // whether or not recording consent was denied.
        if (acceptedThisCall) {
            startOutcomePolling({ callSid, clinicId });
        }

        // Only poll if the call connected — unanswered/rejected calls produce
        // no recording, so no transcript and no note. A consent-denied call
        // produces no note either; the server discards the partial recording.
        if (acceptedThisCall && callSid && clinicId && !recordingConsentDenied) {
            startGeneratedNotePolling({ callSid, clinicId });
        } else if (recordingConsentDenied) {
            logger.info('twilio:outboundcall skipping generated note polling — recording consent denied', { callSid, clinicId });
        }
    };
    call.on('disconnect', handleTermination);
    call.on('cancel', handleTermination);
    call.on('reject', handleTermination);
    call.on('error', (err) => {
        const callSid = call.parameters?.CallSid ?? null;
        const { clinicId } = store.getState().twilioOutboundCall;
        logger.error('twilio:outboundcall call error', {
            callSid,
            deviceState: device?.state ?? null,
            wasAccepted,
            ...describeTwilioError(err)
        });
        const mapped = friendlyCallError(err);
        dispatch(callError(mapped));
        teardown();
        if (mapped.kind === FAILURE) {
            startErrorRefining({ callSid, clinicId });
        }
    });
}

let fakeOutcomeTimers = [];

function clearFakeOutcomeTimers() {
    fakeOutcomeTimers.forEach(clearTimeout);
    fakeOutcomeTimers = [];
}

function clearFakeTimers() {
    fakeTimers.forEach(clearTimeout);
    fakeTimers = [];
}

function fakeTeardown() {
    clearFakeTimers();
    fakeActive = false;
}

function scheduleFake(fn, atMs) {
    fakeTimers.push(setTimeout(fn, atMs));
}

function fakeStartCall({ clinicId, patientId, patientSnapshot, toNumber, dialNumber, fromNumberDisplay }) {
    if (fakeActive) {
        return;
    }
    fakeActive = true;
    clearFakeOutcomeTimers();
    dispatch(
        callFetchingToken({ clinicId, patientId, patientSnapshot, toNumber, fromNumber: fromNumberDisplay ?? null, adHoc: !!dialNumber })
    );

    // Seed the audio picker with fake devices so the picker UI is exercisable
    // in fake mode without a real Twilio Device. Saved IDs from a previous
    // real-mode session won't match these fake ids — filter so the Select
    // doesn't render an out-of-range value.
    const fakeInputDevices = [
        { deviceId: 'default', label: 'Default — System mic' },
        { deviceId: 'fake-mic-1', label: 'USB Headset Mic' }
    ];
    const fakeOutputDevices = [
        { deviceId: 'default', label: 'Default — System speaker' },
        { deviceId: 'fake-speaker-1', label: 'USB Headset' }
    ];
    dispatch(setOutputSelectionSupported(OUTPUT_SELECTION_SUPPORTED));
    dispatch(setAudioDevices({ inputDevices: fakeInputDevices, outputDevices: fakeOutputDevices }));

    const savedInputId = readPreferredDeviceId(LS_INPUT_DEVICE_KEY);
    const savedSpeakerId = readPreferredDeviceId(LS_SPEAKER_DEVICE_KEY);
    const validInputId = fakeInputDevices.some((d) => d.deviceId === savedInputId) ? savedInputId : null;
    const validSpeakerId = fakeOutputDevices.some((d) => d.deviceId === savedSpeakerId) ? savedSpeakerId : null;
    dispatch(setSelectedInputDeviceId(validInputId));
    dispatch(setSelectedOutputDeviceId(validSpeakerId));

    const step = FAKE_DELAY_MS;
    const fakeSid = `CAfake${Date.now()}`;

    scheduleFake(() => dispatch(callConnecting()), step);

    scheduleFake(() => dispatch(callRinging({ callSid: fakeSid })), step * 2);

    if (FAKE_OUTCOME === 'error') {
        // Fails after ringing so the card has a Call SID and the Details /
        // Reference ID section is exercisable in fake mode too.
        scheduleFake(() => {
            dispatch(callError({ kind: FAILURE, message: '[fake] Something went wrong placing this call.', code: 31000 }));
            fakeTeardown();
        }, step * 3);
        return;
    }

    if (FAKE_OUTCOME === 'busy' || FAKE_OUTCOME === 'noanswer') {
        // In real mode these arrive as an SDK error that the post-error
        // server poll upgrades to the honest outcome. Jump straight to that outcome: the card lingers in the
        // neutral state with the outcome copy until the agent dismisses it.
        scheduleFake(() => {
            dispatch(callError(friendlyServerOutcome(FAKE_OUTCOME === 'busy' ? 'BUSY' : 'NO_ANSWER')));
            fakeTeardown();
        }, step * 4);
        return;
    }

    if (FAKE_OUTCOME === 'ring') {
        // Stay ringing forever until the user hangs up.
        return;
    }

    // Default: 'answer' — ring then bridge.
    scheduleFake(() => dispatch(callInProgress({ callSid: fakeSid, startedAt: Date.now() })), step * 4);
}

function fakeStartGeneratedNotePolling() {
    // Walk the stages so the indicator copy is exercisable in fake mode.
    dispatch(generatedNoteUpdated({ transcriptStatus: 'PENDING', noteStatus: 'PENDING', text: null }));
    setTimeout(() => {
        dispatch(generatedNoteUpdated({ transcriptStatus: 'STORED', noteStatus: 'GENERATING', text: null }));
    }, 1500);
    setTimeout(() => {
        dispatch(
            generatedNoteUpdated({
                transcriptStatus: 'STORED',
                noteStatus: 'READY',
                text: '[fake] Discussed medication adherence with patient. Reviewed dosing schedule and addressed side-effect concerns. Patient agreed to continue current regimen and confirmed follow-up in 2 weeks.'
            })
        );
    }, 3000);
}

// Walks the outcome phases so the card copy is exercisable in fake mode:
// "Finalizing…" → a provisional Voicemail → either a settled Answered (the
// transcript overturned it) or, with FAKE_OUTCOME=review, the prompt.
function fakeStartOutcomePolling() {
    clearFakeOutcomeTimers();
    dispatch(callOutcomeSettling());
    const provisional = { outboundCallId: 0, answeredBy: 'MACHINE', answeredBySource: 'AMD' };
    fakeOutcomeTimers.push(
        setTimeout(() => {
            dispatch(callOutcomeUpdated({ ...provisional, settled: false }));
        }, 1500)
    );
    fakeOutcomeTimers.push(
        setTimeout(() => {
            if (FAKE_OUTCOME === 'review') {
                dispatch(callOutcomeUpdated({ ...provisional, settled: true, needsReview: true, reviewReason: 'AMBIGUOUS_VOICEMAIL' }));
            } else {
                dispatch(callOutcomeUpdated({ ...provisional, answeredBy: 'HUMAN', answeredBySource: 'HEURISTIC', settled: true }));
            }
        }, 4500)
    );
}

function fakeHangup() {
    if (fakeActive) {
        const { status, recordingConsentDenied } = store.getState().twilioOutboundCall;
        const wasAcceptedFake = status === 'inCall';
        dispatch(callEnded());
        fakeTeardown();
        if (wasAcceptedFake) {
            fakeStartOutcomePolling();
        }
        if (wasAcceptedFake && !recordingConsentDenied) {
            fakeStartGeneratedNotePolling();
        }
    }
}

function fakeSetMuted(muted) {
    if (fakeActive) {
        dispatch(setMutedAction(!!muted));
    }
}

/**
 * @param {{
 *   clinicId: number,
 *   patientId: number,
 *   patientSnapshot: { firstName?: string, lastName?: string, phoneNumber?: string, mrn?: string },
 *   toNumber: string | null,
 *   dialNumber?: string | null,
 *   callerId?: string | null,
 *   fromNumberDisplay?: string | null
 * }} args
 *
 * `dialNumber` is the ad-hoc override (in the same `+1 (555) 555-5555` form as
 * stored patient numbers; the server normalizes it before dialing). When set,
 * the server dials it instead of looking the destination up from PatientId.
 * Omitted for a normal patient call, which leaves the connect params
 * byte-for-byte as before. The call is still attributed to PatientId either way
 * (care session, timer, call attempt logging), so an ad-hoc third-party call
 * lands on the patient record.
 *
 * `callerId` is the E.164 caller ID chosen in the "Call from" selector; the
 * server validates it against the clinic's allowed set and rejects the dial on
 * a mismatch — it never silently substitutes. Omitted, the server picks its
 * default (patient's office > clinic > org) and the connect params stay
 * byte-for-byte as before. `fromNumberDisplay` is the same number in display
 * form, carried into the slice for the call card's "From …" line.
 */
export async function startCall({ clinicId, patientId, patientSnapshot, toNumber, dialNumber, callerId, fromNumberDisplay }) {
    if (FAKE_ENABLED) {
        fakeStartCall({ clinicId, patientId, patientSnapshot, toNumber, dialNumber, fromNumberDisplay });
        return;
    }

    if (device || activeCall || starting) {
        // A call is already in progress — refuse to start another. Must be
        // user-visible: a silent return here reads as a dead dial button
        // (the "clicked dial → nothing happened" bucket in support tickets).
        logger.warn('twilio:outboundcall refused — call already in progress', {
            clinicId,
            patientId,
            hasDevice: !!device,
            hasActiveCall: !!activeCall,
            starting
        });
        dispatch(
            openSnackbar({
                open: true,
                message:
                    'Could not start the call — another call is already in progress or still ending. If you don’t see a call, refresh the page.',
                variant: 'alert',
                alert: { color: 'warning' }
            })
        );
        return;
    }
    starting = true;
    resetLifecycleFlags();
    // A fresh call obsoletes any refinement or outcome still polling for the
    // last one.
    stopErrorRefining();
    stopOutcomePolling();

    dispatch(
        callFetchingToken({ clinicId, patientId, patientSnapshot, toNumber, fromNumber: fromNumberDisplay ?? null, adHoc: !!dialNumber })
    );

    logger.info('twilio:outboundcall starting call', {
        clinicId,
        patientId,
        mrn: patientSnapshot.mrn,
        toNumber,
        callerId,
        adHoc: !!dialNumber
    });

    // Preflight the microphone before spending a token fetch or building a
    // Device. The SDK acquires the mic inside call.accept(), which
    // device.connect() fires WITHOUT awaiting — connect resolves first, and on
    // acquisition failure the SDK emits `disconnect` before `error`. Left to
    // that ordering, handleTermination classifies the teardown as a remote
    // hangup ("Call ended before it connected.") and the real 31401 lands after
    // removeAllListeners as an uncaught rejection. Acquiring (and immediately
    // releasing) the mic here turns a denied/broken mic into a deterministic
    // failure with the mapped copy. On a first-ever call this also fronts the
    // browser's permission prompt, so enumerateDevices() later returns labeled
    // devices for the picker.
    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
        // getUserMedia is missing entirely — insecure (http) origin or an
        // ancient browser. The SDK's own acquisition in call.accept() can only
        // fail the same way, so refuse the dial now with honest copy instead
        // of proceeding into the misclassified SDK teardown this preflight
        // exists to prevent. Shaped as a SecurityError: mic capture is blocked
        // by the environment, not a flaky device.
        logger.error('twilio:outboundcall getUserMedia unavailable — refusing dial', { clinicId, patientId });
        starting = false;
        dispatch(callError(friendlyUserMediaError({ name: 'SecurityError' })));
        return;
    }

    // Skip the preflight when mic permission is already granted. Acquiring and
    // immediately releasing a device the SDK re-acquires moments later drives
    // an extra profile switch on Bluetooth headsets (audible artifacts), and
    // on some Chrome/Windows setups the second grab lands while the device is
    // still settling and fails with NotReadableError. In the granted steady
    // state the preflight has nothing left to do — the prompt is fronted and
    // enumerateDevices() already returns labels — and a genuinely broken
    // device then fails SDK-side, where teardown's error sink repaints the
    // card. Firefox rejects the 'microphone' permission name; any query
    // failure falls through to running the preflight.
    let micAlreadyGranted = false;
    try {
        const permission = await navigator.permissions?.query?.({ name: 'microphone' });
        micAlreadyGranted = permission?.state === 'granted';
    } catch (e) {
        // fall through — run the preflight
    }

    if (!micAlreadyGranted) {
        // Preflight the device the call will actually use: the saved input
        // preference gets applied to the Device later, and call.accept()
        // acquires THAT mic — not the system default that `audio: true` opens.
        // Deliberately not an `exact` constraint: if the saved device is
        // unplugged the browser falls back to any available mic instead of
        // rejecting, so a stale saved ID can't fail a call that the SDK's own
        // fallback path would have completed.
        const savedInputId = readPreferredDeviceId(LS_INPUT_DEVICE_KEY);
        // Raced against MIC_PREFLIGHT_TIMEOUT_MS (see the constant's doc) so
        // the await always settles and `starting` always clears. The stop() is
        // chained onto the getUserMedia promise itself rather than run after
        // the race: if the timeout wins and the user grants late, the stream
        // still gets released instead of leaving the mic indicator lit.
        let preflightTimeoutId = null;
        let preflightTimedOut = false;
        try {
            await Promise.race([
                navigator.mediaDevices.getUserMedia({ audio: savedInputId ? { deviceId: savedInputId } : true }).then((stream) => {
                    stream.getTracks().forEach((track) => track.stop());
                }),
                new Promise((resolve, reject) => {
                    preflightTimeoutId = setTimeout(() => {
                        preflightTimedOut = true;
                        reject(new Error('Timed out waiting for microphone access'));
                    }, MIC_PREFLIGHT_TIMEOUT_MS);
                })
            ]);
        } catch (err) {
            starting = false;
            if (!cancelDuringStart) {
                if (preflightTimedOut) {
                    // `error`, not `warn`: the dial actually failed, and error
                    // is the level that flushes immediately — a batched entry
                    // here is lost if the user closes the tab on the prompt.
                    logger.error('twilio:outboundcall microphone preflight timed out', {
                        clinicId,
                        patientId,
                        timeoutMs: MIC_PREFLIGHT_TIMEOUT_MS
                    });
                    dispatch(callError({ kind: FAILURE, message: MIC_TIMEOUT_COPY, code: null }));
                } else {
                    // Log the mapped payload too — its synthetic 31401/31402
                    // never reaches the card (no Call SID yet, so no Details
                    // section) and never appears in any SDK event, so this
                    // line is the only place that code exists for triage.
                    const mapped = friendlyUserMediaError(err);
                    logger.error('twilio:outboundcall microphone preflight failed', {
                        clinicId,
                        patientId,
                        error: err?.message,
                        name: err?.name,
                        code: mapped.code
                    });
                    dispatch(callError(mapped));
                }
            }
            return;
        } finally {
            clearTimeout(preflightTimeoutId);
        }
        if (cancelDuringStart) {
            logger.info('twilio:outboundcall cancelled during mic preflight', { clinicId, patientId });
            starting = false;
            return;
        }
    }

    let token;
    try {
        const response = await fetchTwilioAccessToken();
        logger.debug('twilio:outboundcall fetched access token');
        token = response.data.token;
    } catch (err) {
        starting = false;
        if (!cancelDuringStart) {
            logger.error('twilio:outboundcall failed to fetch access token', {
                error: err?.message
            });
            // The axios interceptor collapses timeouts and network failures
            // into the same opaque rejection — one friendly message covers both.
            dispatch(callError({ kind: FAILURE, message: SERVER_UNREACHABLE_COPY, code: null }));
        }
        return;
    }

    if (cancelDuringStart) {
        logger.info('twilio:outboundcall cancelled during token fetch', { clinicId, patientId });
        starting = false;
        return;
    }

    try {
        // Local reference so async callbacks (tokenWillExpire, scheduled
        // refresh) operate on the device they were registered for, not
        // whatever the module-level `device` happens to be after a teardown
        // or a subsequent startCall.
        const myDevice = new Device(token, { logLevel: 'warn', tokenRefreshMs: TOKEN_REFRESH_LEAD_MS });
        device = myDevice;
        myDevice.on('tokenWillExpire', async () => {
            try {
                const refreshed = await fetchTwilioAccessToken();
                // Bail if the device this refresh was scheduled for has been
                // torn down (or replaced by a subsequent call) while the token
                // fetch was in flight — otherwise we'd updateToken on a
                // stale/destroyed Device, or push the previous call's token
                // onto a new one.
                if (device !== myDevice) {
                    return;
                }
                logger.debug('twilio:outboundcall token refreshed', { patientId });
                myDevice.updateToken(refreshed.data.token);
            } catch (err) {
                // Surface but don't kill the call — the existing signaling
                // channel is still valid until the original token's hard
                // expiry.
                logger.error('twilio:outboundcall tokenWillExpire refresh failed', {
                    patientId,
                    error: err?.message,
                    code: err?.code
                });
            }
        });
        myDevice.on('error', (err) => {
            // Snapshot before teardown() nulls activeCall.
            const callSid = activeCall?.parameters?.CallSid ?? null;
            logger.error('twilio:outboundcall device error', {
                patientId,
                callSid,
                deviceState: myDevice.state ?? null,
                ...describeTwilioError(err)
            });
            const mapped = friendlyCallError(err);
            dispatch(callError(mapped));
            teardown();
            if (mapped.kind === FAILURE) {
                startErrorRefining({ callSid, clinicId });
            }
        });

        // Apply persisted audio device preferences (and surface the device list
        // to the slice for the picker) before connecting. Must run after
        // `new Device(...)` because device.audio is only populated then.
        // Failures fall back to defaults silently so the call still proceeds.
        await initializeAudioFromPreferences();
        attachDeviceChangeListener();

        if (cancelDuringStart) {
            logger.info('twilio:outboundcall cancelled during audio init', { clinicId, patientId });
            teardown();
            return;
        }

        dispatch(callConnecting());
        logger.info('twilio:outboundcall connecting', { clinicId, patientId });
        // PatientId attributes the call server-side (care session / timer /
        // attempt log); DialedNumber overrides the destination and CallerId
        // the origin (see startCall doc).
        const params = { PatientId: String(patientId) };
        if (dialNumber) {
            params.DialedNumber = dialNumber;
        }
        if (callerId) {
            params.CallerId = callerId;
        }
        // Race connect() against CONNECT_TIMEOUT_MS (see the constant's doc).
        // On timeout the reject lands in the outer catch: callError is
        // dispatched and teardown() destroys the Device, which also kills the
        // orphaned connect attempt if it ever wakes up. The race keeps a
        // handler attached to the losing connect promise, so its late
        // rejection never surfaces as an unhandled rejection.
        let connectTimeoutId = null;
        let pendingCall;
        try {
            pendingCall = await Promise.race([
                myDevice.connect({ params }),
                new Promise((resolve, reject) => {
                    connectTimeoutId = setTimeout(() => {
                        logger.warn('twilio:outboundcall connect timed out', { clinicId, patientId, timeoutMs: CONNECT_TIMEOUT_MS });
                        reject(new Error('Timed out connecting to the calling service. Check your internet connection and try again.'));
                    }, CONNECT_TIMEOUT_MS);
                })
            ]);
        } finally {
            clearTimeout(connectTimeoutId);
        }

        if (cancelDuringStart) {
            // Hangup arrived between `dispatch(callConnecting())` and the
            // connect promise resolving. Drop the orphaned Call without
            // wiring listeners; the terminal state has already been flipped
            // to `ended` by hangup().
            logger.info('twilio:outboundcall cancelled during connect', { clinicId, patientId });
            try {
                // This Call never got wireCallEvents, so it has no `error`
                // listener at all — attach a sink before discarding it so an
                // SDK error emitted after our disconnect (EventEmitter throws
                // on listener-less `error` emits) can't become an uncaught
                // rejection. Logged so the discarded call's failure is still
                // visible in triage.
                pendingCall.on('error', (err) => {
                    logger.warn('twilio:outboundcall error on cancelled call', {
                        clinicId,
                        patientId,
                        callSid: pendingCall.parameters?.CallSid ?? null,
                        ...describeTwilioError(err)
                    });
                });
                pendingCall.disconnect();
            } catch (e) {
                // ignore — teardown destroys the device anyway
            }
            teardown();
            return;
        }

        activeCall = pendingCall;
        wireCallEvents(activeCall);
    } catch (err) {
        if (cancelDuringStart) {
            // The reject is downstream of our own cancellation (device
            // destroyed mid-connect). UI already shows `ended`; don't
            // overwrite it with `error`.
            teardown();
            return;
        }
        logger.error('twilio:outboundcall failed to start call', {
            clinicId,
            patientId,
            deviceState: device?.state ?? null,
            ...describeTwilioError(err)
        });
        dispatch(callError(friendlyCallError(err)));
        teardown();
    } finally {
        starting = false;
    }
}

export function hangup() {
    if (FAKE_ENABLED) {
        fakeHangup();
        return;
    }

    // Mark the upcoming `disconnect` event as locally initiated so the call
    // event handler dispatches callEnded (silent dismissal) rather than
    // callRemoteDisconnect (lingering card).
    localHangupRequested = true;
    if (activeCall) {
        logger.info('twilio:outboundcall hangup — disconnecting active call', {
            callSid: activeCall.parameters?.CallSid ?? null
        });
        try {
            activeCall.disconnect();
        } catch (e) {
            // ignore — disconnect handler will run teardown
        }
    } else if (starting) {
        // startCall is mid-flight (token fetch or device.connect()) and
        // there's no Call to disconnect yet. Flag the in-flight startCall to
        // tear down its own Device + pending Call once its awaited work
        // settles — calling teardown() here would destroy the Device out
        // from under an in-progress connect() and cause its promise to
        // reject through the catch.
        logger.info('twilio:outboundcall hangup — cancelling in-flight start');
        cancelDuringStart = true;
        dispatch(callEnded());
    } else {
        logger.info('twilio:outboundcall hangup — no active call');
        teardown();
        dispatch(callEnded());
    }
}

export function setMuted(muted) {
    if (FAKE_ENABLED) {
        fakeSetMuted(muted);
        return;
    }

    if (activeCall) {
        try {
            activeCall.mute(!!muted);
            logger.debug('twilio:outboundcall mute toggled', {
                callSid: activeCall.parameters?.CallSid ?? null,
                muted: !!muted
            });
            dispatch(setMutedAction(!!muted));
        } catch (e) {
            logger.warn('twilio:outboundcall mute failed', { error: e?.message, muted: !!muted });
        }
    }
}

export function sendDigits(digits) {
    if (FAKE_ENABLED) {
        logger.debug('twilio:outboundcall fake sendDigits', { digits });
        return;
    }

    const { status } = store.getState().twilioOutboundCall;
    if (!canSendDigitsStatus(status) || !activeCall) {
        return;
    }

    try {
        activeCall.sendDigits(String(digits));
        logger.debug('twilio:outboundcall sent digits', {
            callSid: activeCall.parameters?.CallSid ?? null,
            digits: String(digits),
            status
        });
    } catch (e) {
        logger.warn('twilio:outboundcall sendDigits failed', { error: e?.message, digits: String(digits) });
    }
}

// Patient declined recording mid-call. The server stops the Twilio recording
// and discards the partial recording/transcript, so no note is generated
// (handleTermination also skips note polling once the flag is set). Mid-call
// only — the server responds 409 once the call has ended. Idempotent and
// retryable: a failed attempt leaves the button enabled for another try.
export async function denyRecordingConsent() {
    const { status, callSid, clinicId, recordingConsentDenied, denyRecordingConsentInFlight } = store.getState().twilioOutboundCall;
    if (status !== 'inCall' || recordingConsentDenied || denyRecordingConsentInFlight) {
        return;
    }

    if (FAKE_ENABLED) {
        dispatch(denyRecordingConsentPending());
        setTimeout(() => dispatch(denyRecordingConsentSucceeded()), FAKE_DELAY_MS);
        return;
    }

    if (!callSid || !clinicId) {
        logger.warn('twilio:outboundcall deny recording consent refused — missing callSid or clinicId', { callSid, clinicId });
        return;
    }

    dispatch(denyRecordingConsentPending());
    try {
        await denyRecordingConsentApi(clinicId, callSid);
        logger.info('twilio:outboundcall recording consent denied — recording stopped', { callSid, clinicId });
        dispatch(denyRecordingConsentSucceeded());
    } catch (err) {
        logger.error('twilio:outboundcall deny recording consent failed', {
            callSid,
            clinicId,
            error: err?.message,
            code: err?.code
        });
        dispatch(denyRecordingConsentFailed());
        dispatch(
            openSnackbar({
                open: true,
                message: 'Could not stop the recording — try again',
                variant: 'alert',
                alert: { color: 'error' }
            })
        );
    }
}

// The provider's answer to the post-call prompt ("We logged this call as a
// voicemail. Did you speak with someone?"): 'HUMAN' or 'MACHINE'. Either
// answer is saved — a "Voicemail" confirmation is kept too. Only valid while
// the card is in the review phase; a failed save returns to it for a retry.
export async function submitCallOutcome(answeredBy) {
    const { clinicId, patientId, outcome } = store.getState().twilioOutboundCall;
    if (outcome.phase !== 'review') {
        return;
    }

    if (FAKE_ENABLED) {
        dispatch(callOutcomeSaving());
        setTimeout(() => dispatch(callOutcomeSaved({ answeredBy, answeredBySource: 'USER' })), FAKE_DELAY_MS);
        return;
    }

    const { outboundCallId } = outcome;
    dispatch(callOutcomeSaving());
    try {
        const response = await setOutboundCallAnsweredBy(clinicId, outboundCallId, answeredBy);
        logger.info('twilio:outboundcall call outcome saved', { clinicId, outboundCallId, answeredBy });
        // The reducer ignores this if the card was dismissed mid-save.
        dispatch(
            callOutcomeSaved({
                answeredBy: response?.data?.answeredBy ?? answeredBy,
                answeredBySource: response?.data?.answeredBySource ?? 'USER'
            })
        );
        // The answer can flip the patient's communication streak in the
        // header. Only while still on that patient's page: the details request
        // takes its clinic from the URL.
        if (patientId && window.location.pathname.includes(`/patient/${patientId}/`)) {
            dispatch(getPatientDetails(patientId));
        }
    } catch (err) {
        logger.error('twilio:outboundcall call outcome save failed', {
            clinicId,
            outboundCallId,
            answeredBy,
            error: err?.message,
            code: err?.code
        });
        dispatch(callOutcomeSaveFailed());
        dispatch(
            openSnackbar({
                open: true,
                message: 'Could not save your answer — try again',
                variant: 'alert',
                alert: { color: 'error' }
            })
        );
    }
}

// Picker UI calls these — each persists to localStorage on success and updates
// the slice. `null` means "browser/OS default" and clears the saved value.
// On SDK failure we clear the saved value (so a non-functional id doesn't get
// re-applied next call) and surface a snackbar so the user knows their pick
// didn't take.
function notifyAudioPickFailed(kind) {
    dispatch(
        openSnackbar({
            open: true,
            message: `Could not switch ${kind} — using system default`,
            variant: 'alert',
            alert: { color: 'warning' }
        })
    );
}

export async function setInputDevice(deviceId) {
    if (FAKE_ENABLED) {
        writePreferredDeviceId(LS_INPUT_DEVICE_KEY, deviceId || null);
        dispatch(setSelectedInputDeviceId(deviceId || null));
        return;
    }
    if (!device?.audio) {
        return;
    }
    try {
        if (deviceId) {
            await device.audio.setInputDevice(deviceId);
            writePreferredDeviceId(LS_INPUT_DEVICE_KEY, deviceId);
            dispatch(setSelectedInputDeviceId(deviceId));
            logger.info('twilio:outboundcall input device selected', { deviceId });
        } else {
            try {
                await device.audio.unsetInputDevice?.();
            } catch (e) {
                // ignore
            }
            writePreferredDeviceId(LS_INPUT_DEVICE_KEY, null);
            dispatch(setSelectedInputDeviceId(null));
            logger.info('twilio:outboundcall input device cleared (system default)');
        }
    } catch (err) {
        writePreferredDeviceId(LS_INPUT_DEVICE_KEY, null);
        dispatch(setSelectedInputDeviceId(null));
        notifyAudioPickFailed('microphone');
        logger.error('twilio:outboundcall setInputDevice failed', { deviceId, error: err?.message });
    }
}

export async function setSpeakerDevice(deviceId) {
    if (FAKE_ENABLED) {
        if (!OUTPUT_SELECTION_SUPPORTED) {
            return;
        }
        writePreferredDeviceId(LS_SPEAKER_DEVICE_KEY, deviceId || null);
        dispatch(setSelectedOutputDeviceId(deviceId || null));
        return;
    }
    if (!device?.audio || !OUTPUT_SELECTION_SUPPORTED) {
        return;
    }
    try {
        const target = deviceId || 'default';
        await device.audio.speakerDevices.set([target]);
        await device.audio.ringtoneDevices.set([target]);
        writePreferredDeviceId(LS_SPEAKER_DEVICE_KEY, deviceId || null);
        dispatch(setSelectedOutputDeviceId(deviceId || null));
        logger.info('twilio:outboundcall speaker device selected', { deviceId: deviceId || null, target });
    } catch (err) {
        writePreferredDeviceId(LS_SPEAKER_DEVICE_KEY, null);
        dispatch(setSelectedOutputDeviceId(null));
        notifyAudioPickFailed('speaker');
        logger.error('twilio:outboundcall setSpeakerDevice failed', { deviceId, error: err?.message });
    }
}

// Polled by the picker UI (~20Hz) into component-local state. Kept off the
// Redux slice to avoid 20 dispatches/sec while the menu is open.
export function getInputVolume() {
    if (FAKE_ENABLED) {
        // A gently animated value so the meter visibly moves in fake mode.
        return 0.2 + 0.4 * (0.5 + 0.5 * Math.sin(Date.now() / 250));
    }
    return latestInputVolume;
}

export function resetCallState() {
    if (FAKE_ENABLED) {
        fakeTeardown();
        clearFakeOutcomeTimers();
        dispatch(callCardDismissed());
        return;
    }

    teardown();
    stopErrorRefining();
    stopOutcomePolling();
    dispatch(callCardDismissed());
}

// Patient page unmount/switch — abandon any pipeline tied to the prior patient
// so its READY dispatch can't leak onto the next patient's care note.
export function cancelGeneratedNotePolling() {
    stopNotePolling();
    dispatch(generatedNoteCleared());
}
